import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiArrowSquareOutBold, PiFileXlsBold, PiMagnifyingGlassBold, PiSealWarningDuotone } from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import SellerPodium from '@v2/components/ranking/SellerPodium';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { SortTh, useSortableRows } from '@v2/components/table/useSortableRows';

type ExceptionType = 'exception' | 'floor';
type RankMetric = 'count' | 'usage_pct' | 'commission_difference';

type Row = {
    deal_id: number;
    sale_number: string | null;
    deal_url: string;
    client: string | null;
    seller: string | null;
    finish_date: string | null;
    amount: number;
    discount_pct: number | null;
    types: ExceptionType[];
    rule_rate_pp: number | null;
    paid_rate_pp: number | null;
    commission_difference: number;
    strike_points: number;
    sale_cancelled: boolean;
};

type RankRow = {
    position: number;
    seller: string;
    user_id: number | null;
    avatar_url: string | null;
    count: number;
    exceptions: number;
    floors: number;
    sales: number;
    usage_pct: number;
    amount: number;
    commission_difference: number;
    avg_discount: number | null;
    strike_points: number;
};

type Summary = {
    from: string;
    to: string;
    sales: number;
    deals_with_exception: number;
    usage_pct: number;
    exceptions: number;
    floors: number;
    amount: number;
    commission_difference: number;
    avg_discount: number | null;
    ranking: RankRow[];
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`);

const fmtDate = (iso?: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR') : '—');

const iso = (d: Date) => d.toISOString().slice(0, 10);

function presetRange(key: 'year' | '12m' | 'quarter' | 'month'): { from: string; to: string } {
    const today = new Date();
    const to = iso(today);
    if (key === 'year') return { from: `${today.getFullYear()}-01-01`, to };
    if (key === 'month') return { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to };
    if (key === 'quarter') return { from: iso(new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)), to };
    return { from: iso(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate() + 1)), to };
}

const TYPE_STYLE: Record<ExceptionType, string> = {
    exception: 'bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200',
    floor: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
};

function Kpi({ label, value, hint, tone = 'neutral' }: { label: string; value: string; hint?: string; tone?: 'neutral' | 'violet' | 'amber' | 'red' }) {
    const tones = {
        neutral: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
        violet: 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30',
        amber: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30',
        red: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30',
    };

    return (
        <div className={cn('rounded-xl border p-4', tones[tone])}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{label}</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-gray-900 dark:text-zinc-50">{value}</p>
            {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
        </div>
    );
}

export default function SalesExceptionsPage() {
    const { t } = useI18n();
    const [preset, setPreset] = useState<'year' | '12m' | 'quarter' | 'month' | 'custom'>('year');
    const [range, setRange] = useState(presetRange('year'));
    const [search, setSearch] = useState('');
    const [type, setType] = useState<ExceptionType | ''>('');
    const [metric, setMetric] = useState<RankMetric>('count');
    const [exporting, setExporting] = useState(false);

    const params = useMemo(() => ({
        from: range.from,
        to: range.to,
        search: search.trim() || undefined,
        type: type || undefined,
    }), [range, search, type]);

    const query = useQuery({
        queryKey: ['sales-exceptions', params],
        queryFn: () => api.get('/sales-exceptions', { params }).then((r) => r.data as { data: Row[]; summary: Summary }),
        placeholderData: (prev) => prev,
    });

    const summary = query.data?.summary;
    const rows = query.data?.data ?? [];
    const saleKey = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : v);
    const { sorted, sort, toggle } = useSortableRows(rows, {
        sale: (r) => saleKey(r.sale_number),
        client: (r) => r.client,
        seller: (r) => r.seller,
        won: (r) => r.finish_date,
        amount: (r) => r.amount,
        discount: (r) => r.discount_pct,
        type: (r) => r.types.join(','),
        rate: (r) => r.paid_rate_pp,
        difference: (r) => r.commission_difference,
    }, { key: 'won', dir: 'desc' });

    const ranking = useMemo(() => {
        const list = [...(summary?.ranking ?? [])].sort((a, b) => (b[metric] - a[metric]) || (b.count - a.count));
        return list.slice(0, 10).map((row, i) => ({ ...row, position: i + 1 }));
    }, [summary, metric]);
    const top = Math.max(0.01, Math.abs(ranking[0]?.[metric] ?? 1));

    const metricValue = (row: RankRow) => {
        if (metric === 'usage_pct') return pct(row.usage_pct);
        if (metric === 'commission_difference') return brl(row.commission_difference);
        return t('v2.sales_exceptions.count_value', { count: row.count });
    };

    const pickPreset = (key: 'year' | '12m' | 'quarter' | 'month') => {
        setPreset(key);
        setRange(presetRange(key));
    };

    const exportExcel = async () => {
        setExporting(true);
        try {
            const response = await api.get('/sales-exceptions/export', { params, responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `excecoes-vendas-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error(t('v2.defaulted.export_failed'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader title={t('v2.sales_exceptions.title')} subtitle={t('v2.sales_exceptions.subtitle')}>
                <Button type="button" size="sm" variant="outline" isLoading={exporting} onClick={() => void exportExcel()}>
                    <PiFileXlsBold className="mr-1 size-4" aria-hidden /> {t('v2.defaulted.export')}
                </Button>
            </PageHeader>

            <div className="flex flex-wrap items-center gap-2">
                {(['month', 'quarter', 'year', '12m'] as const).map((key) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => pickPreset(key)}
                        className={cn(
                            'rounded-full border px-3 py-1 text-xs font-semibold',
                            preset === key ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-zinc-900' : 'border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300',
                        )}
                    >
                        {t(`v2.sales_exceptions.preset_${key}`)}
                    </button>
                ))}
                <input type="date" value={range.from} onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, from: e.target.value })); }}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900" />
                <span className="text-xs text-gray-400">→</span>
                <input type="date" value={range.to} onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, to: e.target.value })); }}
                    className="rounded-lg border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-900" />
            </div>

            {query.isLoading || !summary ? (
                <LoadingState variant="dashboard" />
            ) : (
                <>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <Kpi label={t('v2.sales_exceptions.kpi_usage')} value={pct(summary.usage_pct)}
                            hint={t('v2.sales_exceptions.kpi_usage_hint', { count: summary.deals_with_exception, sales: summary.sales })} tone="violet" />
                        <Kpi label={t('v2.sales_exceptions.kpi_types')} value={`${summary.exceptions} · ${summary.floors}`}
                            hint={t('v2.sales_exceptions.kpi_types_hint')} tone="amber" />
                        <Kpi label={t('v2.sales_exceptions.kpi_difference')} value={brl(summary.commission_difference)}
                            hint={t('v2.sales_exceptions.kpi_difference_hint')} tone={summary.commission_difference > 0 ? 'red' : 'neutral'} />
                        <Kpi label={t('v2.sales_exceptions.kpi_amount')} value={brl(summary.amount)}
                            hint={t('v2.sales_exceptions.kpi_discount_hint', { discount: pct(summary.avg_discount) })} />
                    </div>

                    <SellerPodium
                        title={t('v2.sales_exceptions.ranking_title')}
                        hint={t('v2.sales_exceptions.ranking_hint')}
                        valueClass="text-violet-700 dark:text-violet-300"
                        barClass="bg-violet-500"
                        onPick={(row) => setSearch(row.name)}
                        headerRight={(
                            <div className="flex gap-1 rounded-lg bg-gray-100 p-1 text-[11px] dark:bg-zinc-800">
                                {(['count', 'usage_pct', 'commission_difference'] as const).map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => setMetric(m)}
                                        className={cn('rounded-md px-2.5 py-1 font-semibold', metric === m ? 'bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-50' : 'text-gray-500')}
                                    >
                                        {t(`v2.sales_exceptions.metric_${m}`)}
                                    </button>
                                ))}
                            </div>
                        )}
                        rows={ranking.map((row) => ({
                            key: `${row.position}-${row.seller}`,
                            position: row.position,
                            name: row.seller,
                            avatarUrl: row.avatar_url,
                            value: metricValue(row),
                            meta: t('v2.sales_exceptions.rank_meta', { count: row.count, sales: row.sales, usage: pct(row.usage_pct) }),
                            detail: t('v2.sales_exceptions.rank_line', {
                                exceptions: row.exceptions,
                                floors: row.floors,
                                usage: pct(row.usage_pct),
                                difference: brl(row.commission_difference),
                                discount: pct(row.avg_discount),
                            }),
                            ratio: Math.abs(row[metric]) / top,
                        }))}
                    />
                </>
            )}

            <section className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3 dark:border-zinc-800">
                    <label className="relative min-w-[220px] flex-1">
                        <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" aria-hidden />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('v2.defaulted.search')}
                            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                    {(['exception', 'floor'] as const).map((tp) => (
                        <button
                            key={tp}
                            type="button"
                            onClick={() => setType(type === tp ? '' : tp)}
                            className={cn('rounded-full px-2.5 py-1 text-[11px] font-bold', type === tp ? 'ring-2 ring-gray-900 dark:ring-white' : '', TYPE_STYLE[tp])}
                        >
                            {t(`v2.sales_exceptions.type_${tp}`)}
                        </button>
                    ))}
                    <span className="text-xs text-gray-500">{t('v2.sales_exceptions.rows', { count: rows.length })}</span>
                </div>

                {rows.length === 0 && !query.isLoading ? (
                    <div className="flex flex-col items-center gap-2 py-12 text-sm text-gray-500">
                        <PiSealWarningDuotone className="size-8 text-gray-300" aria-hidden />
                        {t('v2.sales_exceptions.empty')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                                <tr>
                                    <SortTh label={t('v2.defaulted.col_sale')} sortKey="sale" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_client')} sortKey="client" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_seller')} sortKey="seller" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.sales_exceptions.col_won')} sortKey="won" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_amount')} sortKey="amount" sort={sort} onSort={toggle} align="right" />
                                    <SortTh label={t('v2.sales_exceptions.col_discount')} sortKey="discount" sort={sort} onSort={toggle} align="right" />
                                    <SortTh label={t('v2.sales_exceptions.col_type')} sortKey="type" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.sales_exceptions.col_rates')} sortKey="rate" sort={sort} onSort={toggle} align="right" />
                                    <SortTh label={t('v2.sales_exceptions.col_difference')} sortKey="difference" sort={sort} onSort={toggle} align="right" />
                                    <th className="px-3 py-2.5" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                {sorted.map((r) => (
                                    <tr key={r.deal_id} className="hover:bg-gray-50/70 dark:hover:bg-zinc-800/40">
                                        <td className="px-3 py-2">
                                            <Link
                                                to={`${r.deal_url}?tab=commission`}
                                                title={t('v2.defaulted.open_deal')}
                                                className="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-0.5 font-bold tabular-nums text-white hover:bg-primary hover:text-gray-900 dark:bg-zinc-100 dark:text-zinc-900"
                                            >
                                                {r.sale_number ?? '—'}
                                            </Link>
                                            {r.sale_cancelled && <span className="ml-1 text-[10px] font-semibold text-rose-600">{t('v2.defaulted.sale_cancelled')}</span>}
                                        </td>
                                        <td className="max-w-[220px] truncate px-3 py-2" title={r.client ?? ''}>{r.client ?? '—'}</td>
                                        <td className="max-w-[160px] truncate px-3 py-2">{r.seller ?? '—'}</td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtDate(r.finish_date)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">{brl(r.amount)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{pct(r.discount_pct)}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className="flex flex-wrap gap-1">
                                                {r.types.map((tp) => (
                                                    <span key={tp} className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', TYPE_STYLE[tp])}>{t(`v2.sales_exceptions.type_${tp}`)}</span>
                                                ))}
                                                {r.strike_points > 0 && <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold text-white">{t('v2.sales_exceptions.strike')}</span>}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                                            <span className="text-gray-400">{pct(r.rule_rate_pp)}</span>
                                            <span className="mx-1 text-gray-300">→</span>
                                            <span className="font-bold">{pct(r.paid_rate_pp)}</span>
                                        </td>
                                        <td className={cn('whitespace-nowrap px-3 py-2 text-right font-bold tabular-nums', r.commission_difference > 0 ? 'text-red-600' : r.commission_difference < 0 ? 'text-emerald-700' : 'text-gray-500')}>
                                            {r.commission_difference > 0 ? '+' : ''}{brl(r.commission_difference)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Link to={`${r.deal_url}?tab=commission`} className="inline-flex text-gray-600 hover:text-gray-900" title={t('v2.defaulted.open_deal')}>
                                                <PiArrowSquareOutBold className="size-4" aria-hidden />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}
